import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Box, Group, Skeleton, Stack, Text } from "@mantine/core";

import sparkLottieUrl from "../../../assets/spark.lottie?url";
import { Sparkle } from "../Sparkle";
import styles from "./DynamicToneGuidance.module.css";
import { UseUpdateButton } from "./UseUpdateButton";

export interface DynamicToneGuidanceProps {
    isEnabled?: boolean;
    isPending?: boolean;
    isAwaitingData?: boolean;
    hasNewerData?: boolean;
    isFallback?: boolean;
    toneGuidance?: string;
    onShowLatest: () => void;
}

export const DynamicToneGuidance = ({
    isEnabled = false,
    isPending = false,
    isAwaitingData = false,
    hasNewerData = false,
    isFallback = false,
    toneGuidance,
    onShowLatest,
}: DynamicToneGuidanceProps) => {
    if (!isEnabled) return null;

    if (isFallback) {
        return (
            <Stack gap="xs" m="md">
                <Box p="md" bdrs="md" bg="dark.6">
                    <Text size="sm" lh="lg">
                        No personalized benefit available. Continue with Smart Offer flow, cover the 3 core benefits.
                    </Text>
                </Box>
            </Stack>
        );
    }

    if (isPending) {
        return (
            <Stack gap="sm" m="md">
                <Group gap="sm" align="center">
                    <Sparkle color="purple" size={20} />
                    <Text size="sm" c="dimmed">
                        Generating personalized guidance and benefits
                    </Text>
                </Group>
                <Skeleton height={80} />
            </Stack>
        );
    }

    if (isAwaitingData) {
        return (
            <Stack gap="xs" m="md">
                <Group gap="7" align="center" wrap="nowrap">
                    <DotLottieReact src={sparkLottieUrl} autoplay loop style={{ width: 20, height: 20 }} />
                    <Text fw={700} size="sm">
                        Listening for customer cues
                    </Text>
                </Group>
                <Box p="md" bdrs="md" bg="dark.6">
                    <Text size="sm" lh="lg">
                        Personalized scripts appear after we confirm the customer&apos;s devices and lifestyle needs. In
                        the meantime, use the standard script below.
                    </Text>
                </Box>
            </Stack>
        );
    }

    return (
        <Box m="md" className={styles.outerContainer}>
            {hasNewerData && (
                <Group justify="space-between" align="center" wrap="nowrap" className={styles.updateHeader}>
                    <Group gap="xs" align="center" wrap="nowrap">
                        <Box className={styles.greenDot} />
                        <Text size="sm" c="white">
                            New updates to Insights & Core Benefits available
                        </Text>
                    </Group>
                    <UseUpdateButton onClick={onShowLatest} />
                </Group>
            )}
            <Stack gap="sm" p="md" className={styles.container}>
                <Group gap={4} bg="dark.7" bdrs="sm" px={6} py={2} c="gray.3" fz={10} fw={600} w="fit-content">
                    <Sparkle size={11} />
                    <span>Customer insights</span>
                </Group>
                <Text lh={"lg"}>{toneGuidance}</Text>
            </Stack>
        </Box>
    );
};
